import React, { useState, PropsWithChildren, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IControlProps, IFormMeta } from '../types/IFormSettings'
import {
	controlSelector,
	formKeySelector,
} from '../selectors/formBuilder.selector'
import FormTooltip from './FormTooltip'
import { InputTypes } from '../types/InputTypes'
import { isAntiFraudControl } from '../helpers/antiFraudHelper'
import { hasTF21Theme } from '../helpers'
import { markets } from '../constants'
import { updateControlState } from '../actions/formActions'

const hasLongLabel = (label: string) =>
	label.normalize().length > 31 ? ' isLong' : ''

const valueClassHandler = (value: any, cn: string[], type: InputTypes) => {
	let classNames = cn
	if (
		value === '' ||
		(value === false && ['checkbox', 'switch'].indexOf(type) > -1)
	) {
		classNames = classNames.filter((className) => className !== 'has-value')

		return classNames.some((className) => className === 'is-empty')
			? classNames
			: classNames.concat(['is-empty'])
	}

	classNames = classNames.filter((className) => className !== 'is-empty')
	return classNames.some((className) => className === 'has-value')
		? classNames
		: classNames.concat(['has-value'])
}

function FormControlWrapper({
	controlName,
	formName,
	children,
}: PropsWithChildren<IControlProps>) {
	const {
		value,
		isValid,
		type,
		validationMessage,
		description,
		required,
		disabled,
		visible,
		label = '',
		tooltip,
		wrapperClass = '',
		col = 12,
		displayFixedLabel = false,
	} = useSelector(controlSelector(formName, controlName))

	const { formMeta = {} as IFormMeta } =
		useSelector(formKeySelector(formName, 'initialSettings')) || {}
	const dispatch = useDispatch()
	const market = formMeta?.market
	const isTF21 = hasTF21Theme(market, formMeta?.visualTheme)
	const isMultiStep = formMeta?.isMultiStep

	const [baseClasses, setClasses] = useState([
		'field',
		'has-feedback',
		'form-group',
		`control-${type}`,
		value === null || value === undefined || value === ''
			? 'is-empty'
			: 'has-value', // 0 and false are VALID options in our dropdowns.
	])

	useEffect(() => {
		if (visible) return
		if (value !== '') {
			dispatch(
				updateControlState({
					formName,
					controlName: controlName,
					value: '',
				}),
			)
		}
	}, [controlName, dispatch, formName, value, visible])

	if (visible === false) return <></>

	const onFocusHandler = () => {
		if (type === 'slider' || type === 'radio') return

		if (!baseClasses.some((className) => className === 'is-focused')) {
			setClasses(baseClasses.concat(['is-focused']))
		}
	}

	const onBlurHandler = ({
		relatedTarget,
		currentTarget,
	}: React.FocusEvent<HTMLInputElement>) => {
		if (type === 'radio' || type === 'slider') return
		let current = relatedTarget as HTMLElement

		// to not lose the focus inside the element
		while (current?.parentElement) {
			if (current.parentElement === currentTarget) {
				return
			}
			current = current.parentElement
		}

		let newClasses: string[] = baseClasses.filter(
			(className) => className !== 'is-focused',
		)

		newClasses = valueClassHandler(currentTarget.value, newClasses, type)

		setClasses(newClasses)
	}

	const classNamesArr = [...baseClasses]

	if (isValid === false) {
		classNamesArr.push('has-error')
	}

	if (disabled) {
		classNamesArr.push('disabled')
	}

	if (required) {
		classNamesArr.push('required')
	}

	if (market) {
		classNamesArr.push(market.toLowerCase())
	}

	if (
		['chips', 'dropdown', 'switch', 'datePicker', 'phoneNumber'].indexOf(
			type,
		) > -1 ||
		displayFixedLabel
	) {
		classNamesArr.push('label-fixed')
	} else if (type === 'radio') {
		classNamesArr.push('radio-label')
	} else {
		classNamesArr.push('label-floating')
	}

	if (isAntiFraudControl(formMeta.enableCrestt, controlName)) {
		classNamesArr.push('d-none')
	}

	const classNameString = valueClassHandler(value, classNamesArr, type).join(
		' ',
	)

	const validSpan = isValid === false &&
		['checkbox', 'radio', 'switch'].indexOf(type) === -1 && (
			<span error-for={controlName} className="error">
				{validationMessage}
			</span>
		)

	const tooltipJsx = tooltip && <FormTooltip tooltip={tooltip} />

	const labelJsx = ['tiles', 'chips', 'checkbox', 'documentUpload'].indexOf(
		type,
	) === -1 && (
		<label
			className={`control-label${hasLongLabel(label)}`}
			id={`anc_${controlName}`}
			htmlFor={controlName}>
			<span dangerouslySetInnerHTML={{ __html: label }} />
			{required ? <span className="required">*</span> : ''}
			{((market !== markets.germany && !isTF21) || isMultiStep) &&
			type !== 'checkbox'
				? tooltipJsx
				: ''}
		</label>
	)

	const descriptionJsx = (
		<div className="description col-12">
			{(market === markets.germany || isTF21) &&
				!isMultiStep &&
				tooltipJsx}
			<div dangerouslySetInnerHTML={{ __html: description || '' }} />
		</div>
	)

	return (
		<div className={`form-box col-${col} ${wrapperClass}`}>
			<div className={classNameString}>
				{labelJsx}

				<div className="value row">
					{(type === 'checkbox' || type === 'switch') &&
						descriptionJsx}
					<div
						className="inputbox col-12"
						onFocus={onFocusHandler}
						onBlur={onBlurHandler}>
						{children}
						{validSpan}
					</div>

					{type !== 'checkbox' && type !== 'switch' && descriptionJsx}
				</div>
			</div>
		</div>
	)
}

export default FormControlWrapper
